import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import Layout from '../components/Layout';
import { userService } from '../services/userService';
import { UserRole, UserRole as UserRoleEnum, UpdateUserRequest } from '../types/user';

const roleOptions = [
  { value: UserRoleEnum.SystemAdmin, label: 'Sistem Yöneticisi' },
  { value: UserRoleEnum.TenantAdmin, label: 'Tenant Yöneticisi' },
  { value: UserRoleEnum.Viewer, label: 'Görüntüleyici' },
];

const UserForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: UserRoleEnum.Viewer as UserRole,
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && id) {
      loadUser(id);
    }
  }, [isEdit, id]);

  const loadUser = async (userId: string) => {
    try {
      setLoadingData(true);
      setError(null);
      const users = await userService.getAll();
      const user = users.find((u) => u.id === userId);
      if (user) {
        // Role değerini normalize et (hem number hem string enum ismi olarak gelebilir)
        // Backend JsonStringEnumConverter kullandığı için enum'lar string olarak geliyor
        let roleValue: UserRole = UserRoleEnum.Viewer;
        if (typeof user.role === 'number') {
          roleValue = user.role;
        } else if (typeof user.role === 'string') {
          // Önce sayısal değer olup olmadığını kontrol et
          const num = parseInt(user.role, 10);
          if (!isNaN(num) && (num === UserRoleEnum.SystemAdmin || num === UserRoleEnum.TenantAdmin || num === UserRoleEnum.Viewer)) {
            roleValue = num as UserRole;
          } else {
            // String enum isimlerini kontrol et (backend'den "SystemAdmin", "TenantAdmin", "Viewer" olarak geliyor)
            const roleStr = String(user.role).trim();
            const roleLower = roleStr.toLowerCase();
            
            console.log('[UserForm] Parsing role string:', {
              original: user.role,
              trimmed: roleStr,
              lower: roleLower,
              SystemAdmin: roleStr === 'SystemAdmin',
              TenantAdmin: roleStr === 'TenantAdmin',
              Viewer: roleStr === 'Viewer',
            });
            
            // Backend'den gelen enum isimleri: "SystemAdmin", "TenantAdmin", "Viewer"
            if (roleStr === 'SystemAdmin' || roleLower === 'systemadmin' || roleLower === 'system_admin') {
              roleValue = UserRoleEnum.SystemAdmin; // 1
              console.log('[UserForm] Mapped to SystemAdmin (1)');
            } else if (roleStr === 'TenantAdmin' || roleLower === 'tenantadmin' || roleLower === 'tenant_admin') {
              roleValue = UserRoleEnum.TenantAdmin; // 2
              console.log('[UserForm] Mapped to TenantAdmin (2)');
            } else if (roleStr === 'Viewer' || roleLower === 'viewer') {
              roleValue = UserRoleEnum.Viewer; // 3
              console.log('[UserForm] Mapped to Viewer (3)');
            } else {
              // Fallback: Eğer tanınmayan bir değer gelirse, console'a log yaz
              console.warn('[UserForm] Unknown role value:', roleStr, 'defaulting to Viewer');
            }
          }
        }
        
        console.log('[UserForm] Loaded user:', {
          id: user.id,
          username: user.username,
          role: user.role,
          roleType: typeof user.role,
          normalizedRole: roleValue,
        });
        
        setFormData({
          username: user.username,
          email: user.email,
          password: '',
          role: roleValue,
        });
      } else {
        setError('Kullanıcı bulunamadı');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kullanıcı yüklenirken bir hata oluştu');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Role değerini normalize et (güvenlik için)
      const roleValue = typeof formData.role === 'number' 
        ? formData.role 
        : typeof formData.role === 'string' 
        ? parseInt(String(formData.role), 10) 
        : UserRoleEnum.Viewer;
      
      const submitData = {
        ...formData,
        role: roleValue as UserRole,
      };
      
      console.log('[UserForm] Submit data:', {
        id,
        role: submitData.role,
        roleType: typeof submitData.role,
        formDataRole: formData.role,
        formDataRoleType: typeof formData.role,
      });
      
      if (isEdit && id) {
        const updateData: UpdateUserRequest = {
          id,
          username: submitData.username,
          email: submitData.email,
          role: submitData.role,
          ...(formData.password ? { password: formData.password } : {}),
        };
        
        console.log('[UserForm] Update request:', updateData);
        
        await userService.update(updateData);
      } else {
        if (!formData.password) {
          setError('Yeni kullanıcı için şifre gereklidir');
          setLoading(false);
          return;
        }
        await userService.create(submitData);
      }
      navigate('/users');
    } catch (err: any) {
      console.error('[UserForm] Submit error:', err);
      setError(err.response?.data?.message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  if (loadingData) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Oluştur'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Kullanıcı Adı"
            required
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            margin="normal"
            placeholder="Kullanıcı adını giriniz"
          />

          <TextField
            fullWidth
            label="E-posta"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            placeholder="E-posta adresini giriniz"
          />

          <TextField
            fullWidth
            label="Şifre"
            type="password"
            required={!isEdit}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            placeholder="Şifreyi giriniz"
            helperText={isEdit ? 'Değiştirmek için doldurun' : undefined}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Rol</InputLabel>
            <Select
              value={formData.role ?? UserRoleEnum.Viewer}
              onChange={(e) => {
                const targetValue = e.target.value;
                const roleValue = typeof targetValue === 'number' 
                  ? targetValue 
                  : Number(targetValue);
                if (!isNaN(roleValue)) {
                  setFormData({ ...formData, role: roleValue as UserRole });
                }
              }}
              label="Rol"
            >
              {roleOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : isEdit ? 'Güncelle' : 'Oluştur'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Layout>
  );
};

export default UserForm;

